import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Button, Tooltip, Tag, message, Empty, Input, List, Popconfirm } from "antd";
import {
  SaveOutlined, EditOutlined, CheckCircleOutlined, UserOutlined, PrinterOutlined,
  AppstoreOutlined, PictureOutlined, BookOutlined, FolderOpenOutlined, DeleteOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { VenueEvent } from "../../../../utils/api/venueEventsAPI";
import { updateVenueEvent } from "../../../../utils/api/venueEventsAPI";
import type { Reservation } from "../../../../utils/api/reservationsAPI";
import { updateReservation } from "../../../../utils/api/reservationsAPI";
import type { FloorPlanTemplate } from "../../../../utils/api/floorPlanTemplatesAPI";
import {
  getFloorPlanTemplates,
  createFloorPlanTemplate,
  deleteFloorPlanTemplate,
} from "../../../../utils/api/floorPlanTemplatesAPI";
import eventsBg from "../../../../assets/eventsBg.bmp";
import "./EventFloorPlan.scss";

interface TablePos {
  number: number;
  x: number; // % of canvas width
  y: number; // % of canvas height
}

type Props = {
  open: boolean;
  onClose: () => void;
  event: VenueEvent;
  reservations: Reservation[];
  onRefresh: (savedLayout?: TablePos[] | null, savedBg?: "grid" | "image") => void;
  canEdit?: boolean;
};

const TABLE_PX = 60;

const STATUS_COLORS: Record<string, string> = {
  pending: "#f59e0b",
  confirmed: "#3b82f6",
  paid: "#10b981",
  waitlisted: "#7c3aed",
  cancelled: "#ef4444",
  "no-show": "#9ca3af",
  attended: "#06b6d4",
};

function buildDefaultLayout(total: number): TablePos[] {
  const cols = Math.ceil(Math.sqrt(total));
  const rows = Math.ceil(total / cols);
  return Array.from({ length: total }, (_, i) => ({
    number: i + 1,
    x: ((i % cols) + 0.5) / cols * 100,
    y: (Math.floor(i / cols) + 0.5) / rows * 100,
  }));
}

const EventFloorPlan: React.FC<Props> = ({
  open, onClose, event, reservations, onRefresh, canEdit,
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLDivElement>(null);
  const userData = useSelector((state: any) => state.userData.user);
  const rawClientId = userData?.clientId;
  const clientId: string = typeof rawClientId === "object" ? rawClientId?._id : rawClientId;

  const [layout, setLayout] = useState<TablePos[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [selectedRes, setSelectedRes] = useState<Reservation | null>(null);
  const [dragging, setDragging] = useState<{
    num: number; startMouseX: number; startMouseY: number; startX: number; startY: number;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [assigning, setAssigning] = useState(false);

  // Background
  const [bgMode, setBgMode] = useState<"grid" | "image">(event.floorPlanBg || "image");

  // Templates
  const [templates, setTemplates] = useState<FloorPlanTemplate[]>([]);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [applyTemplateOpen, setApplyTemplateOpen] = useState(false);

  // Init layout from saved or generate default
  useEffect(() => {
    if (!open || !event.totalTables) return;
    const saved = event.tableLayout;
    if (saved && saved.length === event.totalTables) {
      setLayout(saved);
    } else {
      setLayout(buildDefaultLayout(event.totalTables));
    }
    setBgMode(event.floorPlanBg || "image");
    setEditMode(false);
    setSelectedRes(null);
  }, [open, event._id, event.totalTables]);

  // Load templates when opening apply modal
  const loadTemplates = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await getFloorPlanTemplates(clientId);
      setTemplates(res.data);
    } catch {
      message.error(t("Failed to load templates"));
    }
  }, [clientId, t]);

  // Table → active reservation map
  const tableMap = new Map<number, Reservation>();
  reservations.forEach((r) => {
    if (r.eventTableNumber && !["cancelled", "no-show"].includes(r.status)) {
      tableMap.set(r.eventTableNumber, r);
    }
  });

  const unassigned = reservations.filter(
    (r) => !r.eventTableNumber && !["cancelled", "no-show"].includes(r.status)
  );

  // ─── Drag table to reposition ─────────────────────────────────────────────────
  const handleTableMouseDown = useCallback((e: React.MouseEvent, num: number) => {
    if (!editMode) return;
    e.preventDefault();
    const pos = layout.find((p) => p.number === num);
    if (!pos) return;
    setDragging({ num, startMouseX: e.clientX, startMouseY: e.clientY, startX: pos.x, startY: pos.y });
  }, [editMode, layout]);

  useEffect(() => {
    if (!dragging) return;
    const handleMouseMove = (e: MouseEvent) => {
      if (!canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const dx = (e.clientX - dragging.startMouseX) / rect.width * 100;
      const dy = (e.clientY - dragging.startMouseY) / rect.height * 100;
      const newX = Math.min(96, Math.max(4, dragging.startX + dx));
      const newY = Math.min(96, Math.max(4, dragging.startY + dy));
      setLayout((prev) => prev.map((p) =>
        p.number === dragging.num ? { ...p, x: newX, y: newY } : p
      ));
    };
    const handleMouseUp = () => setDragging(null);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging]);

  // ─── Print ────────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    const canvasEl = canvasRef.current;
    if (!canvasEl) return;
    const rect = canvasEl.getBoundingClientRect();
    const W = rect.width || 900;
    const H = rect.height || 560;

    const tables = layout.map((pos) => {
      const reservation = tableMap.get(pos.number);
      const cx = pos.x / 100 * W;
      const cy = pos.y / 100 * H;
      const r = TABLE_PX / 2;
      const statusColor = reservation ? (STATUS_COLORS[reservation.status] || "#3b82f6") : "#86efac";
      const bg = reservation ? "#dbeafe" : "#f0fdf4";
      const label = reservation ? reservation.guestName.split(" ")[0] : "Free";
      const count = reservation ? (reservation.ticketsCount || reservation.numberOfGuests || 1) : null;
      return `
        <g transform="translate(${cx},${cy})">
          <circle r="${r}" fill="${bg}" stroke="${statusColor}" stroke-width="2.5"/>
          <text x="0" y="-${r - 9}" text-anchor="middle" font-size="8" fill="#9ca3af" font-weight="700">${pos.number}</text>
          <text x="0" y="5" text-anchor="middle" font-size="11" fill="#1a1a1a" font-weight="700">${label}</text>
          ${count ? `<text x="0" y="17" text-anchor="middle" font-size="9" fill="#6b7280">${count}p</text>` : ""}
        </g>`;
    }).join("");

    const legendItems = [
      { color: "#86efac", label: "Free" },
      { color: "#3b82f6", label: "Confirmed" },
      { color: "#f59e0b", label: "Pending" },
      { color: "#10b981", label: "Paid" },
    ].map((l, i) =>
      `<g transform="translate(${16 + i * 90}, ${H + 28})">
        <circle r="6" fill="${l.color}"/>
        <text x="10" y="4" font-size="11" fill="#374151">${l.label}</text>
      </g>`
    ).join("");

    const svgContent = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H + 50}">
        <defs>
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(99,102,241,0.08)" stroke-width="1"/>
          </pattern>
        </defs>
        <rect width="${W}" height="${H}" fill="url(#grid)" rx="12" ry="12"/>
        <rect width="${W}" height="${H}" fill="none" stroke="#e2e8f0" stroke-width="1.5" rx="12" ry="12"/>
        ${tables}
        ${legendItems}
      </svg>`;

    const win = window.open("", "_blank", "width=1000,height=750");
    if (!win) return;
    win.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>${event.title} — Floor Plan</title>
  <style>
    body { margin: 0; padding: 24px; font-family: sans-serif; background: #fff; }
    h2 { margin: 0 0 4px; font-size: 18px; color: #1a1a1a; }
    p  { margin: 0 0 16px; font-size: 13px; color: #6b7280; }
    svg { max-width: 100%; height: auto; display: block; }
    @media print { body { padding: 0; } }
  </style>
</head>
<body>
  <h2>${event.title}</h2>
  <p>${event.date ? new Date(event.date).toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }) : ""} &bull; ${event.time}</p>
  ${svgContent}
  <script>window.onload = () => { window.print(); }<\/script>
</body>
</html>`);
    win.document.close();
  };

  // ─── Save layout ──────────────────────────────────────────────────────────────
  const handleSaveLayout = async () => {
    setSaving(true);
    try {
      await updateVenueEvent(event._id, { tableLayout: layout, floorPlanBg: bgMode });
      message.success(t("Layout saved"));
      setEditMode(false);
      onRefresh(layout, bgMode);
    } catch {
      message.error(t("Failed to save layout"));
    } finally {
      setSaving(false);
    }
  };

  // ─── Save as template ─────────────────────────────────────────────────────────
  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) return;
    setSavingTemplate(true);
    try {
      await createFloorPlanTemplate(clientId, {
        name: templateName.trim(),
        tableCount: layout.length,
        layout,
      });
      message.success(t("Template saved"));
      setSaveTemplateOpen(false);
      setTemplateName("");
    } catch {
      message.error(t("Failed to save template"));
    } finally {
      setSavingTemplate(false);
    }
  };

  // ─── Apply template ───────────────────────────────────────────────────────────
  const handleApplyTemplate = (tpl: FloorPlanTemplate) => {
    // Rescale layout to match current table count if needed
    if (tpl.tableCount === event.totalTables) {
      setLayout(tpl.layout);
    } else {
      // Apply positions proportionally; add/remove tables as needed
      const count = event.totalTables!;
      const scaled = Array.from({ length: count }, (_, i) => {
        const existing = tpl.layout[i];
        return existing
          ? { number: i + 1, x: existing.x, y: existing.y }
          : { number: i + 1, x: 50, y: 50 };
      });
      setLayout(scaled);
    }
    setApplyTemplateOpen(false);
    message.success(t("Template applied — save layout to keep changes"));
  };

  const handleDeleteTemplate = async (id: string) => {
    try {
      await deleteFloorPlanTemplate(id);
      setTemplates((prev) => prev.filter((t) => t._id !== id));
      message.success(t("Template deleted"));
    } catch {
      message.error(t("Failed to delete template"));
    }
  };

  // ─── Assign reservation to table ──────────────────────────────────────────────
  const handleTableClick = async (num: number) => {
    if (editMode || !selectedRes) return;
    if (tableMap.has(num)) {
      message.warning(t("Table already occupied"));
      return;
    }
    setAssigning(true);
    try {
      await updateReservation(selectedRes._id, { eventTableNumber: num });
      message.success(`${selectedRes.guestName} → ${t("Table")} ${num}`);
      setSelectedRes(null);
      onRefresh();
    } catch {
      message.error(t("Failed to assign table"));
    } finally {
      setAssigning(false);
    }
  };

  const handleRemoveAssignment = async (r: Reservation) => {
    try {
      await updateReservation(r._id, { eventTableNumber: null });
      message.success(t("Table assignment removed"));
      onRefresh();
    } catch {
      message.error(t("Failed to remove assignment"));
    }
  };

  if (!event.totalTables) return null;

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        title={
          <span style={{ fontWeight: 700 }}>
            {event.title} — {t("Floor Plan")}
          </span>
        }
        width="92vw"
        style={{ top: 16 }}
        styles={{ body: { padding: "12px 16px" } }}
        footer={null}
        destroyOnClose
      >
        <div className="efp">
          {/* Toolbar */}
          <div className="efp__toolbar">
            {selectedRes && (
              <Tag color="purple" style={{ fontSize: 13, padding: "4px 10px" }}>
                <CheckCircleOutlined style={{ marginRight: 5 }} />
                {t("Assigning")}: <strong>{selectedRes.guestName}</strong> — {t("click a free table")}
              </Tag>
            )}

            {/* Background toggle */}
            <div className="efp__bg-toggle">
              <Tooltip title={t("Grid background")}>
                <button
                  className={`efp__bg-btn${bgMode === "grid" ? " efp__bg-btn--active" : ""}`}
                  onClick={() => setBgMode("grid")}
                >
                  <AppstoreOutlined />
                </button>
              </Tooltip>
              <Tooltip title={t("Image background")}>
                <button
                  className={`efp__bg-btn${bgMode === "image" ? " efp__bg-btn--active" : ""}`}
                  onClick={() => setBgMode("image")}
                >
                  <PictureOutlined />
                </button>
              </Tooltip>
            </div>

            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <Button icon={<PrinterOutlined />} onClick={handlePrint}>
                {t("Print")}
              </Button>
              {canEdit && (
                editMode ? (
                  <>
                    <Tooltip title={t("Apply a saved floor plan template")}>
                      <Button
                        icon={<FolderOpenOutlined />}
                        onClick={() => { loadTemplates(); setApplyTemplateOpen(true); }}
                      >
                        {t("Templates")}
                      </Button>
                    </Tooltip>
                    <Tooltip title={t("Save current layout as a reusable template")}>
                      <Button
                        icon={<BookOutlined />}
                        onClick={() => setSaveTemplateOpen(true)}
                      >
                        {t("Save as Template")}
                      </Button>
                    </Tooltip>
                    <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSaveLayout}>
                      {t("Save Layout")}
                    </Button>
                    <Button onClick={() => {
                      setEditMode(false);
                      setLayout(event.tableLayout && event.tableLayout.length === event.totalTables
                        ? event.tableLayout
                        : buildDefaultLayout(event.totalTables!));
                    }}>
                      {t("Cancel")}
                    </Button>
                  </>
                ) : (
                  <Button icon={<EditOutlined />} onClick={() => setEditMode(true)}>
                    {t("Edit Layout")}
                  </Button>
                )
              )}
            </div>
          </div>

          <div className="efp__body">
            {/* Canvas */}
            <div
              className={`efp__canvas${bgMode === "image" ? " efp__canvas--image" : ""}`}
              style={bgMode === "image" ? { backgroundImage: `url(${eventsBg})` } : undefined}
              ref={canvasRef}
            >
              {editMode && (
                <div className="efp__canvas-hint">{t("Drag tables to reposition")}</div>
              )}
              {layout.map((pos) => {
                const reservation = tableMap.get(pos.number);
                const isOccupied = !!reservation;
                const isFreeTarget = !editMode && !!selectedRes && !isOccupied;
                const statusColor = reservation ? (STATUS_COLORS[reservation.status] || "#3b82f6") : undefined;

                return (
                  <Tooltip
                    key={pos.number}
                    title={
                      reservation ? (
                        <div>
                          <div style={{ fontWeight: 600 }}>{reservation.guestName}</div>
                          <div style={{ fontSize: 12, opacity: 0.85 }}>{reservation.guestPhone}</div>
                          <div style={{ fontSize: 12 }}>{reservation.ticketsCount || 1} {t("guests")}</div>
                          {canEdit && (
                            <div
                              style={{ marginTop: 4, cursor: "pointer", color: "#fca5a5", fontSize: 12 }}
                              onClick={() => handleRemoveAssignment(reservation)}
                            >
                              ✕ {t("Remove from table")}
                            </div>
                          )}
                        </div>
                      ) : `${t("Table")} ${pos.number} — ${t("Free")}`
                    }
                    placement="top"
                  >
                    <div
                      className={[
                        "efp__table",
                        isOccupied ? "efp__table--occupied" : "efp__table--free",
                        editMode ? "efp__table--drag" : "",
                        isFreeTarget ? "efp__table--target" : "",
                      ].filter(Boolean).join(" ")}
                      style={{
                        left: `calc(${pos.x}% - ${TABLE_PX / 2}px)`,
                        top: `calc(${pos.y}% - ${TABLE_PX / 2}px)`,
                        width: TABLE_PX,
                        height: TABLE_PX,
                        borderColor: isOccupied ? statusColor : undefined,
                      }}
                      onMouseDown={(e) => handleTableMouseDown(e, pos.number)}
                      onClick={() => handleTableClick(pos.number)}
                    >
                      <span className="efp__table-num">{pos.number}</span>
                      {isOccupied && reservation ? (
                        <>
                          <UserOutlined className="efp__table-icon" />
                          <span className="efp__table-name">
                            {reservation.guestName.split(" ")[0]}
                          </span>
                        </>
                      ) : (
                        <span className="efp__table-free-label">{t("Free")}</span>
                      )}
                    </div>
                  </Tooltip>
                );
              })}
            </div>

            {/* Sidebar */}
            <div className="efp__sidebar">
              <div className="efp__sidebar-section">
                <div className="efp__sidebar-title">
                  {t("Unassigned")} <span className="efp__sidebar-count">{unassigned.length}</span>
                </div>
                {unassigned.length === 0 ? (
                  <Empty description={t("All guests assigned")} image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: "12px 0" }} />
                ) : (
                  unassigned.map((r) => (
                    <div
                      key={r._id}
                      className={`efp__chip${selectedRes?._id === r._id ? " efp__chip--selected" : ""}`}
                      onClick={() => setSelectedRes(selectedRes?._id === r._id ? null : r)}
                    >
                      <div className="efp__chip-name">{r.guestName}</div>
                      <div className="efp__chip-meta">
                        <span>{r.ticketsCount || 1} {t("guests")}</span>
                        <span
                          className="efp__chip-status"
                          style={{ background: STATUS_COLORS[r.status] + "22", color: STATUS_COLORS[r.status] }}
                        >
                          {t(r.status.charAt(0).toUpperCase() + r.status.slice(1))}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Legend */}
              <div className="efp__legend">
                <div className="efp__legend-item">
                  <div className="efp__legend-dot" style={{ background: "#10b981" }} />
                  <span>{t("Free")}</span>
                </div>
                <div className="efp__legend-item">
                  <div className="efp__legend-dot" style={{ background: "#3b82f6" }} />
                  <span>{t("Confirmed")}</span>
                </div>
                <div className="efp__legend-item">
                  <div className="efp__legend-dot" style={{ background: "#f59e0b" }} />
                  <span>{t("Pending")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Save as Template modal */}
      <Modal
        open={saveTemplateOpen}
        onCancel={() => { setSaveTemplateOpen(false); setTemplateName(""); }}
        title={t("Save as Template")}
        onOk={handleSaveAsTemplate}
        okText={t("Save")}
        confirmLoading={savingTemplate}
        okButtonProps={{ disabled: !templateName.trim() }}
        width={400}
      >
        <p style={{ color: "#6b7280", marginBottom: 12, fontSize: 13 }}>
          {t("Save the current table positions as a reusable template for future events.")}
          {" "}{t("Tables")}: {layout.length}
        </p>
        <Input
          placeholder={t("Template name, e.g. Main Hall")}
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          onPressEnter={handleSaveAsTemplate}
          autoFocus
        />
      </Modal>

      {/* Apply Template modal */}
      <Modal
        open={applyTemplateOpen}
        onCancel={() => setApplyTemplateOpen(false)}
        title={t("Apply Template")}
        footer={null}
        width={480}
      >
        {templates.length === 0 ? (
          <Empty description={t("No templates saved yet")} style={{ padding: "32px 0" }} />
        ) : (
          <List
            dataSource={templates}
            renderItem={(tpl) => (
              <List.Item
                actions={[
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => handleApplyTemplate(tpl)}
                  >
                    {t("Apply")}
                  </Button>,
                  <Popconfirm
                    title={t("Delete this template?")}
                    onConfirm={() => handleDeleteTemplate(tpl._id)}
                    okText={t("Delete")}
                    okButtonProps={{ danger: true }}
                    cancelText={t("Cancel")}
                  >
                    <Button danger size="small" icon={<DeleteOutlined />} />
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  title={tpl.name}
                  description={`${tpl.tableCount} ${t("tables")}`}
                />
              </List.Item>
            )}
          />
        )}
      </Modal>
    </>
  );
};

export default EventFloorPlan;
